import React from 'react';
import { useQuery, useMutation } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { 
  CheckCircleIcon, 
  ClockIcon, 
  CurrencyEuroIcon,
  ChartBarIcon,
  ArrowRightIcon 
} from '@heroicons/react/24/outline';
import { recommendationService } from '../../services/recommendationService';
import { roadmapService } from '../../services/roadmapService';

interface RecommendationCardsProps {
  assessmentId: string;
}

interface AIAgentRecommendation {
  name: string;
  description: string;
  roiEstimate: string;
  timeToValue: string;
  complexity: 'Low' | 'Medium' | 'High';
  budgetRange: string;
  keyBenefits: string[];
  implementationSteps: string[];
  riskFactors: string[];
}

const RecommendationCards: React.FC<RecommendationCardsProps> = ({ assessmentId }) => {
  const navigate = useNavigate();

  const { data: recommendationsData, isLoading, error } = useQuery(
    ['recommendations', assessmentId],
    () => recommendationService.getRecommendations(assessmentId),
    {
      enabled: !!assessmentId,
      retry: 2,
    }
  );

  const generateRoadmapMutation = useMutation(
    (selectedRecommendation: AIAgentRecommendation) =>
      roadmapService.generateRoadmap(assessmentId, selectedRecommendation),
    {
      onSuccess: (data) => {
        navigate(`/roadmap/${data.id}`);
      },
      onError: (error) => {
        console.error('Error generating roadmap:', error);
      }
    }
  );

  const getComplexityColor = (complexity: string) => {
    switch (complexity) {
      case 'Low':
        return 'bg-green-100 text-green-800';
      case 'Medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'High':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleSelectRecommendation = (recommendation: AIAgentRecommendation) => {
    generateRoadmapMutation.mutate(recommendation);
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow p-8">
        <div className="animate-pulse">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            Raccomandazioni AI Agent
          </h2>
          <div className="space-y-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="border rounded-lg p-6">
                <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
                <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-2/3"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error || !recommendationsData) {
    return (
      <div className="bg-white rounded-lg shadow p-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">
          Raccomandazioni AI Agent
        </h2>
        <div className="text-center py-8">
          <p className="text-gray-600">
            Errore nel caricamento delle raccomandazioni. Riprova più tardi.
          </p>
        </div>
      </div>
    );
  }

  const recommendations = recommendationsData.recommendations || [];

  return (
    <div className="bg-white rounded-lg shadow-lg p-8">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">
          Raccomandazioni AI Agent
        </h2>
        <p className="text-lg text-gray-600">
          I migliori AI Agents per iniziare il tuo percorso nell'intelligenza artificiale
        </p>
      </div>

      {recommendations.length === 0 ? (
        <div className="text-center py-12">
          <ChartBarIcon className="mx-auto h-16 w-16 text-gray-400 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Nessuna raccomandazione disponibile
          </h3>
          <p className="text-gray-600">
            Completa l'assessment per ricevere raccomandazioni personalizzate.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {recommendations.map((recommendation: AIAgentRecommendation, index: number) => (
            <div
              key={index}
              className="border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow duration-300 relative"
            >
              {index === 0 && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-primary-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                    Raccomandato
                  </span>
                </div>
              )}

              <div className="mb-4">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {recommendation.name}
                </h3>
                <p className="text-gray-600">
                  {recommendation.description}
                </p>
              </div>

              <div className="space-y-3 mb-6">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">ROI Stimato</span>
                  <span className="text-lg font-bold text-green-600">
                    {recommendation.roiEstimate}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Time to Value</span>
                  <span className="font-semibold text-gray-900">
                    {recommendation.timeToValue}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Complessità</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getComplexityColor(recommendation.complexity)}`}>
                    {recommendation.complexity}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Budget</span>
                  <span className="font-semibold text-gray-900">
                    {recommendation.budgetRange}
                  </span>
                </div>
              </div>

              <div className="mb-6">
                <h4 className="font-semibold text-gray-900 mb-3">Benefici Principali:</h4>
                <ul className="space-y-2">
                  {recommendation.keyBenefits?.slice(0, 3).map((benefit, i) => (
                    <li key={i} className="flex items-start">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                      <span className="text-sm text-gray-600">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <button
                onClick={() => handleSelectRecommendation(recommendation)}
                disabled={generateRoadmapMutation.isLoading}
                className="w-full bg-primary-600 hover:bg-primary-700 disabled:bg-primary-400 text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center"
              >
                {generateRoadmapMutation.isLoading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  <>
                    Genera Roadmap
                    <ArrowRightIcon className="ml-2 h-4 w-4" />
                  </>
                )}
              </button>
            </div>
          ))}
        </div>
      )}

      {recommendations.length > 0 && (
        <div className="mt-12 bg-gray-50 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">
            💡 Suggerimento
          </h3>
          <p className="text-gray-600">
            Ti consigliamo di iniziare con la prima raccomandazione per massimizzare il ROI e ridurre i rischi. 
            Una volta implementata con successo, potrai espandere con gli altri AI Agents.
          </p>
        </div>
      )}
    </div>
  );
};

export default RecommendationCards;